import { Stack } from "@mui/material";
import deepEqual from "fast-deep-equal";
import type { ConditionFilterDescriptor } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type {
  DraggableMetaType,
  DraggableFieldType,
  FieldWrapper,
  ConditionField,
} from "../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectDimensions } from "../../../store/selectors/metaDataSelectors.ts";
import { selectConditions, selectTabularSettingsState } from "../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../store/thunks/fieldStatesThunks.ts";
import { newDimensionMeta } from "../../../utilities/fieldsHelper.ts";
import DropFieldContainer from "../common/fields/DropFieldContainer.tsx";
import ConditionFieldOptionPopup from "../../viewer/common/conditions/ConditionFieldOptionPopup.tsx";
import { formatCaption } from "../../viewer/common/conditions/conditionsHelper.tsx";
import ConditionDimensionCaption from "../../viewer/common/conditions/ConditionDimensionCaption.tsx";
import type { ConditionFilterOptions } from "../../viewer/common/conditions/ConditionFilterOptionsContext.ts";
import ConditionFilterOptionsContext from "../../viewer/common/conditions/ConditionFilterOptionsContext.ts";
import FieldCaptionWrapper from "../../viewer/common/conditions/FieldCaptionWrapper.tsx";
import SelectionLimitContext from "@entrilia/shared/components/reporting/text-filter-grid/SelectionLimitContext.ts";
import { createNarrowingConditionDescriptor } from "../../../utilities/conditionDescriptorHelper.ts";
import { getKeyValue } from "../common/table/tableHelper.ts";
import { canDropCashFlowSplitField, createCashFlowSplitField } from "./cashFlowMeasureDrop.ts";
import { getCashFlowSettings } from "./getCashFlowSettings.ts";

const acceptedDropTypes = [AreaItemType.DIMENSION];
const keepPosition = () => {};
const singleFieldContainerSx = { minHeight: 0 };
// Kept in step with CashFlowReportConfiguration.MaxSplitValues on the server.
const MAX_SPLIT_VALUES = 10;
const splitFilterOptions: ConditionFilterOptions = {
  operators: [ConditionEqualityType.Equal, ConditionEqualityType.Contains],
};
const formatSplitCaption = (field: ConditionField) =>
  field.config.filter?.values.length ? (
    formatCaption(field)
  ) : (
    <FieldCaptionWrapper
      Caption={<ConditionDimensionCaption field={field} />}
      subCaption="Select Values..."
      subCaptionColor="text.secondary"
    />
  );

interface Props {
  readonly: boolean;
}

const CashFlowSplitDropContainer = ({ readonly }: Props) => {
  const dispatch = useAppDispatch();
  const dimensions = useSelector(selectDimensions);
  const conditions = useSelector(selectConditions);
  const settings = useSelector(selectTabularSettingsState);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const value = getCashFlowSettings(settings);
  const fields = useMemo(() => {
    if (!value.splitBy) {
      return [];
    }
    const meta =
      dimensions.find((dimension) => dimension.name === value.splitBy) ??
      newDimensionMeta({ name: value.splitBy, caption: value.splitBy });
    const field = createCashFlowSplitField(meta);
    if (!field) {
      return [];
    }
    return [{ ...field, config: { ...field.config, filter: value.splitFilter ?? field.config.filter } }];
  }, [dimensions, value.splitBy, value.splitFilter]);
  const narrowingFilters = useMemo(
    () => conditions.map(createNarrowingConditionDescriptor).filter((filter) => filter !== undefined),
    [conditions]
  );
  const update = (splitBy: string | undefined) => {
    dispatch(
      fieldsStateActions.updateSettings({ settings: { cashFlow: { ...value, splitBy, splitFilter: undefined } } })
    );
    setAnchorEl(null);
  };
  const saveFilter = (filter: ConditionFilterDescriptor) => {
    const splitFilter = filter.values.length > 0 ? filter : undefined;
    if (!readonly && !deepEqual(value.splitFilter ?? undefined, splitFilter)) {
      dispatch(fieldsStateActions.updateSettings({ settings: { cashFlow: { ...value, splitFilter } } }));
    }
    setAnchorEl(null);
  };
  const canDrop = useCallback(
    (item: FieldWrapper<DraggableFieldType> | DraggableMetaType) =>
      canDropCashFlowSplitField(item, !!value.splitBy, readonly),
    [value.splitBy, readonly]
  );
  const field = fields[0];
  return (
    <Stack spacing={1}>
      <DropFieldContainer<ConditionField, DraggableMetaType>
        title="Split by"
        maxFields={1}
        areaFieldType={AreaItemType.COLUMNS}
        acceptedDropTypes={acceptedDropTypes}
        fields={fields}
        formatCaption={formatSplitCaption}
        hideAddField={fields.length > 0}
        containerSx={fields.length > 0 ? singleFieldContainerSx : undefined}
        canDropItem={canDrop}
        getKeyValue={getKeyValue}
        onAddItem={(field) => update(field.meta.name)}
        onRemoveItem={() => update(undefined)}
        onMoveItem={keepPosition}
        showOptions={({ ref }) => setAnchorEl(ref)}
        createListItem={createCashFlowSplitField}
        readonly={readonly}
      />
      {anchorEl && field && (
        <ConditionFilterOptionsContext value={splitFilterOptions}>
          <SelectionLimitContext value={MAX_SPLIT_VALUES}>
            <ConditionFieldOptionPopup
              anchorEl={anchorEl}
              field={field}
              narrowingFilters={narrowingFilters}
              save={saveFilter}
              onRemoveItem={() => update(undefined)}
              canBeRemoved={!readonly}
              readonly={readonly}
              readonlyCustomLabel
              resizable
              CustomId={undefined}
            />
          </SelectionLimitContext>
        </ConditionFilterOptionsContext>
      )}
    </Stack>
  );
};

export default CashFlowSplitDropContainer;
